<template>
  <div>
    <page-header
      :actions="headerActionsConfig"
      left-side-class="col-12 col-md-7"
      :page="headerConfig"
      right-side-class="col-12 col-md-5"
    />
    <search-request-filter v-model="filterModel" @on-search="onSearchHandler" />

    <custom-separator class="my-4" text="Resultado de Contratos" />

    <custom-result-not-found v-if="results.length == 0" />

    <div v-for="(row, index) of results" :key="`request-service-item-${index}`">
      <search-request-row
        :data="row"
        :is-compensatory-service="row.isCompensatory"
        :not-approved-for-printing="serviceNotApprovedForPrinting(row)"
        :signed-with-another-company="isServiceSignedWithAnotherCompany(row)"
        @on-select-service="onSelectServiceHandler(row)"
      />
      <row-message
        v-if="
          isServiceSignedWithAnotherCompany(row) &&
            row.requestServiceContract != undefined &&
            row.requestServiceContract != null &&
            row.statusId != serviceStatus.NotAvailable
        "
        :text="serviceSignedWithAnotherCompanyMessage(row)"
        warning
      />
      <row-message
        v-if="serviceNotApprovedForPrinting(row)"
        text="El servicio no se encuentra marcado como impreso por la USPQRP."
        warning
      />
      <row-message
        v-if="
          row.requestServiceContract != undefined &&
            row.requestServiceContract != null &&
            row.statusId == serviceStatus.NotAvailable
        "
        :text="
          'El servicio no está disponible debido a que no tiene balance de servicio compensatorio pendiente ya que los mismos fueron facturados en otro contrato.'
        "
        warning
      />
      <row-message
        v-if="
          row.requestServiceStatusId == 2 &&
            row.statusId == 7 &&
            !isServiceSignedWithAnotherCompany(row) &&
            !serviceNotApprovedForPrinting(row)
        "
        :text="
          `La evaluación ${!row.wasBilling ? 'no ha sido' : 'ha sido'} facturada
          ${!row.wasBilling ? '' : 'en la factura ' + row.billNumber}.`
        "
        warning
      />
      <row-message
        v-if="row.isCompensatory"
        :text="
          `Servicio cuenta con planilla de servicios compensatorios ${row.compensatoryRequestNumber}.`
        "
        warning
      />
    </div>
    <pagination
      v-if="showPagination"
      :data="pagination"
      show-text
      @page-size-change="changePageSize"
      @pagination-go-page="goToPageHandler"
    />
  </div>
</template>

<script>
//libs components
import PageHeader from 'PageHeader';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';

import rpContractStatus from '@/utils/constants/rpContractStatus.js';

//local components
import { SearchRequestFilter, SearchRequestRow } from './components';
import RowMessage from '@/components/common/RowMessage.vue';

//services
import { searchRequestServices as _searchRequestServices } from '@/services/uspqrp/contract.api.js';

//utils
import storeMixin from '@quisit/mipe-utils/mixins/storeGenerator';

//constants
import storeModuleNames from '@/utils/constants/storeModuleNames.js';
import studentStatuses from '@/utils/constants/studentStatuses';
const DEFAULT_PAGE_SIZE = 10;
const CONTRACT_MANAGEMENT_ROUTE_NAME = 'contractManagement';
const ALL = 0;
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';

export default {
  name: 'ContractManagement',
  mixins: [alertMixin, storeMixin],
  components: {
    PageHeader,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    SearchRequestFilter,
    SearchRequestRow,
    RowMessage,
  },
  data: () => ({
    moduleName: storeModuleNames.UspqrpContractSearchRequestModuleName,
    filterModel: {},
    results: [],
    pagination: {
      currentPage: 1,
      totalItemCount: 1,
      pageCount: 1,
      count: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    },
  }),
  computed: {
    headerConfig() {
      return {
        title: 'Manejo de Contratos para Servicios',
        icon: 'fas fa-clipboard-list',
        routes: [
          'Inicio',
          'Proveedores',
          'Contrato',
          'Manejo de Contratos para Servicios',
        ],
      };
    },
    headerActionsConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left fa-fw',
          variant: 'outline-success',
          click: () => this.onGoBackHandler(),
        },
      ];
    },
    showPagination() {
      return this.pagination.totalItemCount > this.pagination.pageSize;
    },
    companyId() {
      return this.$route.params?.companyId;
    },
    isServiceSignedWithAnotherCompany() {
      return (row) =>
        row.requestServiceContract?.contractStatusId &&
        this.companyId !== row.requestServiceContract?.companyId;
    },
    serviceNotApprovedForPrinting() {
      return (row) => !row.approvedForPrinting;
    },
    serviceSignedWithAnotherCompanyMessage() {
      return (row) =>
        `El servicio ya se encuentra en contrato con el proveedor ${row.requestServiceContract?.companyName}.`;
    },
    serviceStatus() {
      return rpContractStatus;
    },
  },
  async beforeRouteLeave(to, _, next) {
    if (to?.name !== CONTRACT_MANAGEMENT_ROUTE_NAME) {
      this.resetData();
    }
    next();
  },
  destroyed() {
    this.persistData();
  },
  async mounted() {
    if (Object.values(this.filterModel).length) {
      await this.onSearchHandler({
        requestNumber: this.filterModel.requestNumber,
        type: this.filterModel.type.id,
      });
    }
  },
  methods: {
    async searchRequestServices(requestNumber, isAvailable = ALL) {
      const payload = {
        companyId: this.companyId,
        isAvailable,
        requestNumber,
        pageIndex: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
      };

      const { data: response } = await _searchRequestServices(payload);

      if (Object.values(response).length) {
        this.results = response.data;
        this.pagination = {
          ...this.pagination,
          totalItemCount: response.totalItemCount,
          pageCount: response.pageCount,
          currentPage: response.currentPage,
          count: response.count,
        };
      }
    },
    async onSearchHandler({ requestNumber }) {
      if (requestNumber) {
        await this.getRequestServices(requestNumber);
      }
    },
    async goToPageHandler(page) {
      this.pagination.currentPage = page;
      await this.getRequestServices(this.filterModel.requestNumber);
    },
    async getRequestServices(requestNumber) {
      await this.searchRequestServices(
        requestNumber,
        this.filterModel.type?.id
      );
    },
    onSelectServiceHandler({
      requestServiceId,
      studentStatusId,
      studentStatusName,
    }) {
      const invalidStatusId = [
        studentStatuses.Inactive,
        studentStatuses.NotEligible,
        studentStatuses.Egress,
        studentStatuses.Available,
      ];
      if (invalidStatusId.includes(studentStatusId)) {
        this.ShowToast(
          '',
          `El estudiante no puede ser seleccionado, ya que se encuentra (${studentStatusName}).`,
          'error'
        );
        return;
      }

      if (requestServiceId) {
        this.$router.push({
          name: CONTRACT_MANAGEMENT_ROUTE_NAME,
          params: {
            companyId: this.companyId,
            requestServiceId,
          },
          query: {
            isNewContract: true,
          },
        });
      }
    },
    async changePageSize(size) {
      this.pagination.pageSize = size;
      await this.getRequestServices(this.filterModel.requestNumber);
    },
    resetData() {
      Object.assign(this.$data, this.$options.data());
    },
    onGoBackHandler() {
      this.resetData(); //reset to initial component data
      this.$router.go(-1);
    },
  },
};
</script>
